import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, formatISO, parseISO, startOfTomorrow, isValid } from 'date-fns'
import { ArrowLeft, Lock } from 'lucide-react'
import { CLASH_STORAGE_KEYS, CLASH_ASSETS } from '../../lib/clashConstants'
import { isWordInWordList } from '../../lib/words'
import {
  fetchAdminPassword,
  fetchGameConfig,
  updateGameConfig,
  WordSchedule,
} from '../../lib/firebase'

export const Admin = () => {
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [wordSchedule, setWordSchedule] = useState<WordSchedule | null>(null)
  const [newWord, setNewWord] = useState('')
  const [scheduleType, setScheduleType] = useState<'immediate' | 'tomorrow'>('tomorrow')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoadingPassword, setIsLoadingPassword] = useState(true)
  const [firebasePassword, setFirebasePassword] = useState<string | null>(null)
  const [isLocked, setIsLocked] = useState(false)

  // Helper function to safely format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Unknown date'
    try {
      const date = parseISO(dateString)
      if (!isValid(date)) return 'Invalid date'
      return format(date, 'MMM d, yyyy')
    } catch {
      return 'Invalid date'
    }
  }

  // Load Firebase password on mount
  useEffect(() => {
    const loadPassword = async () => {
      try {
        const pwd = await fetchAdminPassword()
        setFirebasePassword(pwd)
      } catch (e) {
        console.error('Failed to load admin password:', e)
        setError('Failed to connect to server')
      } finally {
        setIsLoadingPassword(false)
      }
    }
    loadPassword()
  }, [])

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (!firebasePassword || isLoadingPassword) return

      const auth = sessionStorage.getItem(CLASH_STORAGE_KEYS.adminAuth)
      if (auth === firebasePassword) {
        setIsAuthenticated(true)
        await loadWordSchedule()
      }
    }
    checkAuth()
  }, [firebasePassword, isLoadingPassword])

  const loadWordSchedule = async () => {
    try {
      const config = await fetchGameConfig()
      setWordSchedule(config)
      setIsLocked(config?.isLocked ?? false)
    } catch (e) {
      console.error('Error loading word schedule:', e)
      setError('Failed to load word schedule from server')
    }
  }

  const saveWordSchedule = async (schedule: WordSchedule) => {
    try {
      await updateGameConfig(schedule)
      setWordSchedule(schedule)
      // Also cache in localStorage for offline fallback
      localStorage.setItem(CLASH_STORAGE_KEYS.currentWord, JSON.stringify(schedule))
    } catch (e) {
      console.error('Error saving word schedule:', e)
      setError('Failed to save word schedule')
      throw e
    }
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (!firebasePassword) {
      setError('Unable to verify password. Please try again.')
      return
    }

    if (password === firebasePassword) {
      sessionStorage.setItem(CLASH_STORAGE_KEYS.adminAuth, firebasePassword)
      setIsAuthenticated(true)
      loadWordSchedule()
      setError('')
    } else {
      setError('Incorrect password')
    }
  }

  const handleLogout = () => {
    sessionStorage.removeItem(CLASH_STORAGE_KEYS.adminAuth)
    setIsAuthenticated(false)
    navigate('/')
  }

  const handleSetWord = async () => {
    const word = newWord.trim().toUpperCase()

    if (word.length !== 5) {
      setError('Word must be exactly 5 letters')
      return
    }

    if (!isWordInWordList(word)) {
      setError('Word not found in valid word list')
      return
    }

    if (!wordSchedule) return

    try {
      if (scheduleType === 'immediate') {
        // Set as current word immediately
        const newSchedule: WordSchedule = {
          currentWord: word,
          currentWordDate: formatISO(new Date(), { representation: 'date' }),
          nextWord: null,
          nextWordScheduledFor: null,
        }
        await saveWordSchedule(newSchedule)
        setSuccessMessage(`Word set to "${word}" immediately! Players can play now.`)
        setNewWord('')

        // Clear game state to allow players to play again
        localStorage.removeItem('gameState')
      } else {
        // Schedule for tomorrow at midnight
        const tomorrow = startOfTomorrow()
        const newSchedule: WordSchedule = {
          ...wordSchedule,
          nextWord: word,
          nextWordScheduledFor: formatISO(tomorrow),
        }
        await saveWordSchedule(newSchedule)
        setSuccessMessage(`Word "${word}" scheduled for ${format(tomorrow, 'MMM d, yyyy')} at midnight`)
        setNewWord('')
      }

      setError('')
      setTimeout(() => setSuccessMessage(''), 5000)
    } catch (e) {
      setError('Failed to update word. Please try again.')
    }
  }

  const handleCancelScheduled = async () => {
    if (!wordSchedule) return

    try {
      const newSchedule: WordSchedule = {
        ...wordSchedule,
        nextWord: null,
        nextWordScheduledFor: null,
      }
      await saveWordSchedule(newSchedule)
      setSuccessMessage('Scheduled word cancelled')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (e) {
      setError('Failed to cancel scheduled word')
    }
  }

  const handleToggleLockdown = async () => {
    if (!wordSchedule) return

    try {
      const newLockState = !isLocked
      const newSchedule: WordSchedule = {
        ...wordSchedule,
        isLocked: newLockState,
      }
      await saveWordSchedule(newSchedule)
      setIsLocked(newLockState)
      setSuccessMessage(newLockState ? 'Game locked - lockdown screen enabled' : 'Game unlocked - players can access')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (e) {
      setError('Failed to toggle lockdown')
    }
  }

  // Show loading state while fetching password
  if (isLoadingPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clash-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Show error if password couldn't be loaded
  if (!firebasePassword) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="text-center">
          <p className="text-red-600">Unable to connect to authentication server.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-clash-blue text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
              <Lock className="h-6 w-6 text-slate-600" />
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-slate-900">
                Admin Access
              </h1>
              <p className="text-sm text-slate-500">
                Enter the admin password to continue
              </p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full rounded-xl border border-slate-200
                         bg-white px-4 py-3 text-base text-slate-800
                         focus:border-clash-blue focus:outline-none
                         focus:ring-2 focus:ring-clash-blue/50"
            />
            {error && <p className="text-sm text-clash-red">{error}</p>}
            <button
              type="submit"
              className="w-full rounded-pill bg-gradient-to-r from-slate-900
                         to-slate-800 py-3 font-semibold text-white
                         shadow-md transition-all hover:shadow-lg"
            >
              Access Admin
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-5 flex w-full items-center justify-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Game
          </button>

          <div className="mt-6 flex justify-center">
            <img
              src={CLASH_ASSETS.occLogo}
              alt="Roslyn OCC Games"
              className="h-10 w-auto object-contain opacity-80"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header - Clash Style */}
      <header className="clash-header">
        <div className="header-left">
          <img src={CLASH_ASSETS.clashLogo} alt="CLASH" className="header-logo" />
        </div>
        <div className="header-center">
          <span className="header-title">Word Manager</span>
        </div>
        <div className="header-right">
          <button
            onClick={handleLogout}
            className="header-btn px-4"
            style={{ width: 'auto', borderRadius: '26px' }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Admin Content */}
      <main className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        {/* Success Message */}
        {successMessage && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4">
            <p className="text-green-800 font-medium text-center">{successMessage}</p>
          </div>
        )}

        {/* Lockdown Toggle */}
        <div className="rounded-modal bg-white p-6 shadow-lg border-2 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Game Lockdown</h2>
              <p className="text-sm text-gray-600 mt-1">
                {isLocked
                  ? 'Game is currently locked - players see lockdown screen'
                  : 'Game is accessible - players can play normally'}
              </p>
            </div>
            <button
              onClick={handleToggleLockdown}
              className={`rounded-pill px-6 py-3 font-bold text-white shadow-md hover:shadow-lg transition-all ${
                isLocked
                  ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600'
                  : 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600'
              }`}
            >
              {isLocked ? 'Unlock Game' : 'Lock Game'}
            </button>
          </div>
        </div>

        {/* Current Word Display */}
        <div className="rounded-modal bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-xl font-bold text-gray-800">Current Word</h2>
          {wordSchedule && (
            <div className="space-y-3">
              <div className="rounded-lg bg-clash-blue/10 p-6 text-center">
                <p className="text-4xl font-black tracking-wider text-clash-blue">
                  {wordSchedule.currentWord}
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Set on {formatDate(wordSchedule.currentWordDate)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Next Scheduled Word */}
        {wordSchedule?.nextWord && (
          <div className="rounded-modal bg-white p-6 shadow-lg border-2 border-yellow-200">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="mb-4 text-xl font-bold text-gray-800">Next Scheduled Word</h2>
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-yellow-600">
                    {wordSchedule.nextWord}
                  </p>
                  {wordSchedule.nextWordScheduledFor && (
                    <p className="text-sm text-gray-600">
                      Will activate on {formatDate(wordSchedule.nextWordScheduledFor)} at midnight
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleCancelScheduled}
                className="rounded-lg bg-red-50 px-4 py-2 text-sm font-medium
                           text-red-600 hover:bg-red-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Set New Word Form */}
        <div className="rounded-modal bg-white p-6 shadow-lg">
          <h2 className="mb-6 text-xl font-bold text-gray-800">Set New Word</h2>

          <div className="space-y-4">
            {/* Word Input */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Enter 5-letter word
              </label>
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value.slice(0, 5).toUpperCase())}
                placeholder="HELLO"
                maxLength={5}
                className="w-full rounded-lg border border-gray-300
                           bg-white px-4 py-3 text-2xl font-bold uppercase tracking-wider
                           text-center text-gray-800
                           focus:border-clash-blue focus:outline-none
                           focus:ring-2 focus:ring-clash-blue/50"
              />
            </div>

            {/* Schedule Type */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                When to activate
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setScheduleType('immediate')}
                  className={`rounded-lg border-2 px-4 py-3 font-medium transition-all ${
                    scheduleType === 'immediate'
                      ? 'border-clash-blue bg-clash-blue text-white shadow-md'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  Immediately
                </button>
                <button
                  onClick={() => setScheduleType('tomorrow')}
                  className={`rounded-lg border-2 px-4 py-3 font-medium transition-all ${
                    scheduleType === 'tomorrow'
                      ? 'border-clash-blue bg-clash-blue text-white shadow-md'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  Tomorrow at Midnight
                </button>
              </div>
              {scheduleType === 'immediate' && (
                <p className="mt-2 text-sm text-yellow-700">
                  ⚠️ This will clear all players' game state and let them play immediately
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <p className="text-sm text-red-600 font-medium">{error}</p>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSetWord}
              disabled={newWord.length !== 5}
              className="w-full rounded-pill bg-gradient-to-r from-clash-blue
                         to-blue-400 py-3 font-bold text-white hover:from-blue-500
                         hover:to-blue-300 disabled:opacity-50
                         disabled:cursor-not-allowed shadow-md hover:shadow-lg
                         transition-all"
            >
              {scheduleType === 'immediate' ? 'Set Word Now' : 'Schedule for Tomorrow'}
            </button>
          </div>
        </div>

        {/* Help Info */}
        <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
          <p className="font-semibold mb-2">How it works:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li><strong>Immediate:</strong> Sets the word right now and clears game state so players can play again</li>
            <li><strong>Tomorrow at Midnight:</strong> Schedules the word to automatically switch at 12:00 AM</li>
            <li>Only one word can be scheduled at a time</li>
            <li>Words must be valid 5-letter words from the game dictionary</li>
          </ul>
        </div>
      </main>
    </div>
  )
}
